import { Attr, AttrMap, IndexedMesh } from "./indexed-mesh";
import { IndexedMeshWriter } from "./indexed-mesh-writer";


export class Group {
    start: number;
    count: number;
    materialIndex: number;

    constructor(start: number, count: number, index: number) {
        this.start = start;
        this.count = count;
        this.materialIndex = index;
    }
}

type Groupable = {
    addGroup: (start: number, count: number, materialIndex: number) => void;
}

export class Join<T extends AttrMap> extends IndexedMeshWriter<T> {

    items: IndexedMeshWriter<T>[];
    meshes: IndexedMesh<T>[];

    groups: Group[];

    constructor(...items: IndexedMeshWriter<T>[]) {
        super();
        this.items = items;
    }

    computeAttributeCounts(): IndexedMesh<T> {
        const
            attrs = {} as AttrMap,
            result = new IndexedMesh(attrs);

        this.meshes = this.items.map(i => i.computeAttributeCounts());

        for(const m of this.meshes) {
            result.index.count += m.index.count;
            result.position.count += m.position.count;

            // Join doesn't require any attributes aside from position and
            // index, but we still want to merge attributes if they're preset
            for(const name of Object.keys(m.attrs)) {
                const a = m.attrs[name];
                if(!(name in attrs)) {
                    attrs[name] = new Attr(a.size);
                }
                attrs[name].count = result.position.count;
            }
        }

        return result as IndexedMesh<T>;
    }

    computeMeshData(result: IndexedMesh<T>) {
        this.delegateMeshDataComputation(result);

        // All the mesh data is now in the `result` mesh's attributes as well
        // as our own---both are updated because the subarrays are backed by
        // the same buffer, only the indexes have been shifted for convenince.
        // Now we have to update the index _values_ because they refer to
        // individual writers' 0 point, which will be different in the context
        // of the larger index at this Join's level.

        const
            meshes = this.meshes,
            idx = result.index.data,
            groups: Group[] = this.groups = [];

        // Define several accumulators that track our cumulative positions in
        // various arrays
        let
            // i -> idx[i]
            i = 0,
            // p -> pos[p*3], i.e. the element index, not the raw data index
            // p -> uv[p*2]
            p = 0,
            // m -> groups[m], i.e. the material index in THREE.BufferGeometry
            m = 0;

        for(const mesh of meshes) {
            groups.push(new Group(i, mesh.index.count, m));

            const midx = mesh.index.data;
            for(let j = 0; j < midx.length; j++) {
                idx[i] = p + midx[j];
                i++;
            }

            p += mesh.position.count;
            m++;
        }
    }

    /**
     * The Join class tracks the indices in the result so that each input mesh
     * can have a separate material index in the output. You do this by passing
     * the THREE.BufferGeometry instance here, e.g.
     *
     * ```
     * const
     *     join = new Join(...),
     *     geo = generateBufferGeometry(join);
     * join.assignGroups(geo);
     *
     * ```
     */
    assignGroups(geo: Groupable) {
        for(const group of this.groups) {
            geo.addGroup(group.start, group.count, group.materialIndex);
        }
    }

    /**
     * First step in the Join's computeMeshData() is to pass all the mesh
     * generation tasks down to the component meshe writers. For each mesh
     * writer we're joining, we create a slice of our data (subarray) that they
     * will write into.
     */
    private delegateMeshDataComputation(result: IndexedMesh<T>) {
        const
            items = this.items,
            meshes = this.meshes,
            idx = result.index.data,
            pos = result.position.data,
            attrs = result.attrs,
            anames = Object.keys(attrs);
            
        let
            // i -> idx[i]
            i = 0,
            // p -> pos[p]
            p = 0,
            // f[name] -> mesh.attrs[*].data[f[name]]
            f = Object.fromEntries(anames.map(name => [name, 0]));

        for(let m = 0; m < meshes.length; m++) {
            const
                item = items[m],
                mesh = meshes[m],
                idxEnd = i + mesh.index.count * mesh.index.size,
                posEnd = p + (mesh.position.count * mesh.position.size);
            mesh.index.data = idx.subarray(i, idxEnd);
            i = idxEnd;
            mesh.position.data = pos.subarray(p, posEnd);
            p = posEnd;

            for(const name of anames) {
                if(name in mesh.attrs) {
                    const
                        attr: Attr = attrs[name],
                        mattr: Attr = mesh.attrs[name],
                        attrStart = f[name],
                        attrEnd = attrStart + (mattr.count * mattr.size);
                    mattr.data = attr.data.subarray(attrStart, attrEnd);
                    f[name] = attrEnd;
                } else {
                    // See the comment above the corresponding line in the
                    // computeMeshData method
                    f[name] += mesh.position.count * attrs[name].size;
                }
            }

            item.computeMeshData(mesh);
        }
    }
};

